<template>
    <div
        v-if="isActive"
        :class="overlayClasses"
        :style="overlayStyle"
        @click="handleOverlayClick"
    >
        <div class="loading-spinner-container">
            <slot>
                <ProgressSpinner
                    style="width: 50px; height: 50px"
                    stroke-width="4"
                />
            </slot>
        </div>
    </div>
</template>

<script>
import ProgressSpinner from 'primevue/progressspinner';
/**
 * ELoading - Abstraction wrapper around PrimeVue's ProgressSpinner
 *
 * Usage:
 *   <e-loading v-model:active="isLoading" :is-full-page="false">
 *     <e-icon icon="sync" size="is-large" custom-class="fa-spin" />
 *   </e-loading>
 *
 *   <e-loading :active="isLoading" :is-full-page="true">
 *     <ProgressSpinner />
 *   </e-loading>
 *
 *   <div class="container">
 *     <e-loading :active="isLoading" :is-full-page="false"></e-loading>
 *     <p>Content that will be covered by loading overlay</p>
 *   </div>
 *
 *   <e-loading v-model="isLoading" :can-cancel="true">
 *     Loading with cancel option
 *   </e-loading>
 *
 * All Buefy b-loading props, events, and slots are supported.
 */
export default {
    name: 'ELoading',
    components: {
        ProgressSpinner,
    },
    inheritAttrs: false,
    emits: ['update:modelValue', 'update:active'],
    computed: {
        isActive() {
            const attrs = this.$attrs || {};

            if (attrs.modelValue !== undefined || attrs['model-value'] !== undefined) {
                return attrs.modelValue ?? attrs['model-value'];
            }

            if (attrs.active !== undefined || attrs['is-active'] !== undefined) {
                return attrs.active ?? attrs['is-active'];
            }

            return false;
        },
        isFullPage() {
            return this.$attrs['is-full-page'] ?? this.$attrs.isFullPage ?? true;
        },
        canCancel() {
            return this.$attrs['can-cancel'] ?? this.$attrs.canCancel ?? false;
        },
        overlayClasses() {
            return [
                'e-loading-overlay',
                {
                    'is-full-page': this.isFullPage,
                },
            ];
        },
        overlayStyle() {
            const backgroundColor =
                this.$attrs.backgroundColor ?? this.$attrs['background-color'] ?? 'rgba(255, 255, 255, 0.8)';

            return {
                backgroundColor,
            };
        },
    },
    methods: {
        handleOverlayClick() {
            if (this.canCancel) {
                // Emit both update events for compatibility
                this.$emit('update:modelValue', false);
                this.$emit('update:active', false);
            }
        },
    },
};
</script>

<style scoped>
.e-loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 999;
    cursor: default;
}

.e-loading-overlay.is-full-page {
    position: fixed;
    z-index: 9999;
}

.loading-spinner-container {
    display: flex;
    align-items: center;
    justify-content: center;
}

</style>
